import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { eventCheckInStore } from 'Views/Components/_HumanWritten/CheckIn/EventCheckIn/EventContext/EventCheckInContext';
import { checkInStore } from '../Views/Components/_HumanWritten/CheckIn/FerryCheckIn/context/CheckInContext';

interface CheckInRoutesResult {
	current: string;
	schedule: string;
	eventDetailedView: string;
	base: string;
	baseWithSearch: string;
	passengers: string;
	/** The fare-type step of the add flow, after the passenger details. */
	seating: string;
	scan: string;
	summary: string;
	standby: string;
	standbyAdd: string;
	parking: string;
	parkingAdd: string;
	history: string;
	goToSummary(): void;
	goToParkingReservation(id: string): void;
	goToBase(): void;
	replaceToBase(): void;
	goToSelectCustomer(): void;
	replaceToSelectCustomer(): void;
	goToAdd(): void;
	replaceToAdd(): void;
	goToEdit(bookingId: string): void;
	goToPassengerEdit(bookingId: string): void;
	goToSeatingEdit(bookingId: string): void;
	goToMove(): void;
	goToStandbyList(): void;
	goToParkingList(): void;
	/** Check-in's History screen, optionally already searching (a booking reference, say). */
	goToHistory(search?: string): void;
}

export function useCheckInRoutes(isEventBooking: boolean = false): CheckInRoutesResult {
	const location = useLocation();
	const navigate = useNavigate();

	const routes = React.useMemo<CheckInRoutesResult>(() => {
		const path = location.pathname;
		const base = `/${isEventBooking ? 'event-' : ''}check-in/${
			isEventBooking ? eventCheckInStore.eventId : checkInStore.ferryTripId
		}`;
		// Use EventSeriesId for the detailed view URL, fallback to eventId if not available
		const eventSeriesId = eventCheckInStore.eventDetails?.eventSeriesId ?? eventCheckInStore.eventId;

		return {
			current: path,
			schedule: '/ferry-schedule',
			eventDetailedView: `/event-details/${eventSeriesId}`,
			base: base,
			baseWithSearch: `${base}${location.search}`,
			passengers: `${base}/add/passengers`,
			seating: `${base}/add/seating`,
			scan: `${base}/scan${location.search}`,
			summary: `${base}/summary${location.search}`,
			standby: `${base}/standby`,
			standbyAdd: `${base}/standby/add`,
			parking: `${base}/parking`,
			parkingAdd: `${base}/parking/add`,
			history: `${base}/history`,
			goToSummary() {
				navigate(`${base}/summary${location.search}`);
			},
			goToBase() {
				navigate(`${base}`);
			},
			replaceToBase() {
				navigate(`${base}`, { replace: true });
			},
			goToSelectCustomer() {
				navigate(`${base}/add/select-customer${location.search}`);
			},
			replaceToSelectCustomer() {
				navigate(`${base}/add/select-customer${location.search}`, { replace: true });
			},
			goToAdd() {
				navigate(`${base}/add${location.search}`);
			},
			replaceToAdd() {
				navigate(`${base}/add${location.search}`, { replace: true });
			},
			goToEdit(bookingId: string) {
				navigate(`${base}/booking/${bookingId}${location.search}`);
			},
			goToPassengerEdit(bookingId: string) {
				navigate(`${base}/booking/${bookingId}/passengers`);
			},
			goToSeatingEdit(bookingId: string) {
				// Carrying the flag, unlike the passengers step: the sidebar reads `isEditMode` from
				// the query, and a step that drops it turns into a read-only view the moment the
				// page is reloaded on it.
				navigate(`${base}/booking/${bookingId}/seating?isEditMode=true`);
			},
			goToMove() {
				navigate(`${base}/move${location.search}`);
			},
			goToStandbyList() {
				navigate(`${base}/standby`, { replace: true });
			},
			goToParkingList() {
				navigate(`${base}/parking`, { replace: true });
			},
			goToHistory(search?: string) {
				navigate(search ? `${base}/history?${new URLSearchParams({ search })}` : `${base}/history`);
			},
			goToParkingReservation(id: string) {
				navigate(`${base}/parking/${id}`);
			},
		};
	}, [location.pathname, checkInStore.ferryTripId, location.search, eventCheckInStore.eventDetails?.eventSeriesId]);

	return routes;
}
